import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { CheckCircle, Mail } from 'lucide-react';

const Unsubscribe: React.FC = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (email) {
      handleUnsubscribe();
    }
  }, [email]);

  const handleUnsubscribe = async () => {
    if (!email) {
      setError('No email address provided');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Unsubscribing email:', email);
      
      // Check if already unsubscribed
      const { data: existing } = await supabase
        .from('email_campaign_sends')
        .select('*')
        .eq('email', email)
        .eq('campaign_name', 'unsubscribed')
        .maybeSingle();
      
      if (existing) {
        console.log('Already unsubscribed');
        setSuccess(true);
        return;
      }

      // Add to unsubscribes
      const { data, error: insertError } = await supabase
        .from('email_campaign_sends')
        .insert({
          email: email,
          campaign_name: 'unsubscribed'
        })
        .select();

      console.log('Unsubscribe insert result:', { data, error: insertError });

      if (insertError) {
        console.error('Error recording unsubscribe:', insertError);
        throw insertError;
      }

      setSuccess(true);
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err instanceof Error ? err.message : 'Failed to unsubscribe. Please contact support@framemyhome.ai');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
        {loading ? (
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Processing your request...</p>
          </div>
        ) : success ? (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-gray-900 mb-3">
              You've been unsubscribed
            </h1>
            <p className="text-gray-600 mb-2">
              We've removed <strong>{email}</strong> from our email campaign list.
            </p>
            <p className="text-gray-600 mb-6">
              You won't receive any more marketing emails from us.
            </p>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Changed your mind?
              </p>
              <Link
                to="/"
                className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                Visit FrameMyHome
              </Link>
            </div>
          </div>
        ) : error ? (
          <div className="text-center">
            <Mail className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-gray-900 mb-3">
              Oops! Something went wrong
            </h1>
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            <a
              href="mailto:support@framemyhome.ai"
              className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              Contact Support
            </a>
          </div>
        ) : (
          <div className="text-center">
            <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-gray-900 mb-3">
              Invalid Request
            </h1>
            <p className="text-gray-600 mb-6">
              No email address was provided in the unsubscribe link.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              Go Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;

